// Statuses where we did NOT fight the chargeback (no representment submitted). That is
// exactly `skipped` now: the old `closed_no_evidence` status is gone, since a dispute we
// never submitted evidence for is not a kind of loss. One constant drives the Sankey node,
// its drill, and BOTH the v1 and v2 AllPage "Not fighting" cards, and it stays a
// DESELECTABLE status the filter dropdown can render and clear. Widen here only once the
// dropdown/backend gain the other not-fighting statuses (accepted / auto_accepted / expired).
export const NOT_FIGHTING = ["skipped"]

// The dispute flow, as a lifecycle tree. The first branch is unchanged: did we fight the
// chargeback at all. Below "Fighting" the split is by STAGE, in two steps, so a node is
// never a mix of "where is it now" and "how did it end":
//
//   Fighting -> Resolved   -> Won / Lost          (it is over)
//            -> In progress -> the stage it sits at now
//
// Previously Resolved and the in-progress stages were siblings of one another, which read
// as though finishing were a stage parallel to waiting, and put five ribbons out of one
// node. Every node here has exactly one parent, so the ribbons never cross.
//
// `match` maps the status enum to a leaf, so renaming a status only touches this list, and
// every id is a status the /recover/all filter can render and clear.
export const FLOW_GROUPS = [
  {
    id: "fighting",
    label: "Fighting",
    groups: [
      {
        id: "resolved",
        label: "Resolved",
        // Larger branch first: the flat-top layout (sort={false}, see Sankey.js) places
        // nodes in array order, and the big ribbon reads better on top.
        stages: [
          { id: "won", label: "Won", match: ["won"] },
          // One Lost leaf for both lost statuses, as the status sheet groups them.
          { id: "lost", label: "Lost", match: ["lost_defended", "lost_undefended"] }
        ]
      },
      {
        id: "in_progress",
        label: "In progress",
        stages: [
          { id: "needs_response", label: "Open", match: ["needs_response"] },
          { id: "awaiting_review", label: "Awaiting review", match: ["awaiting_review"] },
          // Everything post-submission, awaiting the issuer's decision.
          { id: "pending_decision", label: "Pending decision", match: ["pending_decision"] },
          // Any status we do not recognise is still a real case in flight, so it lands
          // here rather than being dropped: the root total has to equal the record count,
          // or the Sankey contradicts the donut centre and the KPI row.
          { id: "in_progress_other", label: "Other", match: [] }
        ]
      },
      // Inquiry-only, and an END state, not a stage: the issuer closed the inquiry and is
      // turning it into a formal dispute, so nothing is owed on it and nothing further
      // happens to it (it is in no queue and not an active status anywhere else in the
      // app). It cannot sit under Resolved either: that branch is Won + Lost, the same
      // "resolved" the KPI row counts, and folding escalations in would make the two
      // disagree. So it is its own terminal branch, ending at this column.
      { id: "escalated", label: "Escalated", match: ["escalated"], stages: [] }
    ]
  },
  // No representment submitted, and none coming. Terminates at its own column, one earlier
  // than the fight stages, exactly as before.
  { id: "not_fighting", label: "Not fighting", match: NOT_FIGHTING, groups: [] }
]

const OTHER_STAGE_ID = "in_progress_other"

// Case-type lanes for the split version. Each lane runs the whole tree above on its own,
// so the split stays visible all the way to the outcomes instead of merging back at the
// next column. `prefix` namespaces the node ids per lane.
const TYPE_LANES = [
  { id: "dispute", label: "Disputes", prefix: "d" },
  {
    id: "inquiry",
    label: "Inquiries",
    prefix: "i",
    // Every inquiry is answered (a fast answer is what stops it becoming a chargeback), so
    // the lane carries no Not fighting branch. Listed rather than left to the empty-node
    // filter so the shape states the rule instead of depending on the data happening to
    // agree. If volume ever shows up there anyway, it is still drawn (see below): a flow
    // that hides cases would stop adding up.
    excludeBranches: ["not_fighting"]
  }
]

// Every leaf of the tree, flattened. Three shapes, all terminating where they should:
// a branch with no groups is a leaf itself (Not fighting), a group with stages contributes
// those stages, and a group with no stages is a leaf at the group column (Escalated).
const leavesOf = (branch) =>
  branch.groups?.length
    ? branch.groups.flatMap((group) =>
        group.stages?.length
          ? group.stages.map((stage) => ({ group, stage }))
          : [{ group, stage: { id: group.id, match: group.match || [] } }]
      )
    : [{ group: null, stage: { id: branch.id, match: branch.match || [] } }]

const ALL_LEAVES = FLOW_GROUPS.flatMap((branch) =>
  leavesOf(branch).map(({ group, stage }) => ({ branch, group, stage }))
)

// status -> leaf id. Anything unmapped falls to the catch-all stage.
const LEAF_OF_STATUS = new Map(
  ALL_LEAVES.flatMap(({ stage }) => stage.match.map((status) => [status, stage.id]))
)

// Sankey flow for the "Dispute flow" section. A strict, conserving tree; recharts derives
// node values from the link values, so only the links carry numbers, and empty nodes (with
// their links) are dropped so a flow never shows a zero branch.
//
// `splitByType` runs the same tree once per case type under a shared root:
//   All disputes -> Disputes / Inquiries -> each lane's own branches and stages.
// The lanes never merge, which is the point: an inquiry and a chargeback have different
// lifecycles (an inquiry escalates, it is never lost), and a split that rejoined at the
// next column showed nothing after that column.
//
// `rows` is flow/'s `result`: a count and a volume per case type and status. The tree is laid
// out here rather than on the backend, which only has to count.
export const sankeyFlow = (rows = [], { splitByType = false } = {}) => {
  // counts[type][leafId] and the money behind them. One pass, then every shape below is
  // assembled from it. Volume rides on each node because the Sankey labels a node with both
  // ("<count> | <volume>").
  const counts = {}
  const volumes = {}
  for (const lane of TYPE_LANES) {
    counts[lane.id] = {}
    volumes[lane.id] = {}
    for (const { stage } of ALL_LEAVES) {
      counts[lane.id][stage.id] = 0
      volumes[lane.id][stage.id] = 0
    }
  }
  for (const row of rows) {
    const type = row?.dispute_type === "inquiry" ? "inquiry" : "dispute"
    const leaf = LEAF_OF_STATUS.get(row?.status) || OTHER_STAGE_ID
    counts[type][leaf] += row?.count || 0
    volumes[type][leaf] += row?.amount || 0
  }

  const sum = (laneIds, leafIds) =>
    laneIds.reduce((a, laneId) => a + leafIds.reduce((b, id) => b + counts[laneId][id], 0), 0)
  const sumVolume = (laneIds, leafIds) =>
    laneIds.reduce((a, laneId) => a + leafIds.reduce((b, id) => b + volumes[laneId][id], 0), 0)
  const laneIdsOf = (lane) => (lane ? [lane.id] : TYPE_LANES.map((l) => l.id))
  const branchLeaves = (branch) => leavesOf(branch).map(({ stage }) => stage.id)
  const groupLeaves = (group) =>
    group.stages?.length ? group.stages.map((stage) => stage.id) : [group.id]

  const total = sum(
    TYPE_LANES.map((l) => l.id),
    ALL_LEAVES.map(({ stage }) => stage.id)
  )
  if (total === 0) return { nodes: [], links: [] }

  // Nodes are emitted COLUMN BY COLUMN, and within a column lane by lane: array order is
  // what the flat-top layout uses, so this keeps each lane's band contiguous.
  //
  // Both versions start from one root: the split version answers "of everything that came
  // in, how much is each case type, and where does each type end up", so the shared total
  // is the thing the two lanes are shares OF.
  const totalVolume = sumVolume(
    TYPE_LANES.map((l) => l.id),
    ALL_LEAVES.map(({ stage }) => stage.id)
  )

  const nodes = [{ id: "all", label: "All disputes", v: total, volume: totalVolume }]
  const links = []
  const nodeId = (lane, id) => (lane ? `${lane.prefix}_${id}` : id)
  const lanes = splitByType ? TYPE_LANES : [null]

  if (splitByType) {
    for (const lane of TYPE_LANES) {
      const leafIds = ALL_LEAVES.map(({ stage }) => stage.id)
      const v = sum([lane.id], leafIds)
      const volume = sumVolume([lane.id], leafIds)
      nodes.push({ id: nodeId(lane, "root"), label: lane.label, v, volume })
      links.push({ source: "all", target: nodeId(lane, "root"), value: v, volume })
    }
  }

  // Branch column: Fighting / Not fighting.
  const branchesOf = (lane) =>
    FLOW_GROUPS.filter(
      (branch) =>
        !lane?.excludeBranches?.includes(branch.id) ||
        // Excluded but non-empty: draw it anyway rather than dropping cases.
        sum(laneIdsOf(lane), branchLeaves(branch)) > 0
    )

  for (const lane of lanes) {
    for (const branch of branchesOf(lane)) {
      const v = sum(laneIdsOf(lane), branchLeaves(branch))
      const volume = sumVolume(laneIdsOf(lane), branchLeaves(branch))
      nodes.push({ id: nodeId(lane, branch.id), label: branch.label, v, volume })
      links.push({
        source: splitByType ? nodeId(lane, "root") : "all",
        target: nodeId(lane, branch.id),
        value: v,
        volume
      })
    }
  }

  // A grouping node earns its column only when it actually divides something. If all of a
  // group's volume sits in ONE of its stages, the node restates that stage under another
  // name and pushes everything a column right for nothing. That is exactly the inquiry
  // lane: an inquiry cannot be lost, so its Resolved leads only to Won. Collapse those, per
  // lane, and let the branch link straight to the stage.
  const collapsedGroups = new Set()
  const laneKey = (lane) => (lane ? lane.prefix : "-")
  const liveStages = (lane, group) =>
    (group.stages || []).filter((stage) => sum(laneIdsOf(lane), [stage.id]) > 0)

  // Group column: Resolved / In progress under Fighting. A branch with no groups
  // (Not fighting) contributes nothing here, which is what terminates it a column early.
  for (const lane of lanes) {
    for (const branch of branchesOf(lane)) {
      for (const group of branch.groups || []) {
        const v = sum(laneIdsOf(lane), groupLeaves(group))
        // `group.stages?.length` guard: a group with no stages (Escalated) is a leaf in its
        // own right, not a grouping node, so it is never collapsed.
        if (group.stages?.length && liveStages(lane, group).length <= 1) {
          collapsedGroups.add(`${laneKey(lane)}:${group.id}`)
          continue
        }
        const volume = sumVolume(laneIdsOf(lane), groupLeaves(group))
        nodes.push({ id: nodeId(lane, group.id), label: group.label, v, volume })
        links.push({
          source: nodeId(lane, branch.id),
          target: nodeId(lane, group.id),
          value: v,
          volume
        })
      }
    }
  }

  // Stage column.
  for (const lane of lanes) {
    for (const branch of branchesOf(lane)) {
      for (const group of branch.groups || []) {
        const skipped = collapsedGroups.has(`${laneKey(lane)}:${group.id}`)
        for (const stage of group.stages || []) {
          const v = sum(laneIdsOf(lane), [stage.id])
          const volume = sumVolume(laneIdsOf(lane), [stage.id])
          nodes.push({ id: nodeId(lane, stage.id), label: stage.label, v, volume })
          links.push({
            // A collapsed group is not in the graph, so its stage hangs off the branch.
            source: nodeId(lane, skipped ? branch.id : group.id),
            target: nodeId(lane, stage.id),
            value: v,
            volume
          })
        }
      }
    }
  }

  return {
    // `v` was only the running total each node was assembled from; it is `value` on the
    // links that the chart reads.
    nodes: nodes.filter((n) => n.v > 0).map(({ v: _v, ...n }) => n),
    links: links.filter((l) => l.value > 0)
  }
}
